import { lazy, Suspense } from "react"
import PropTypes from "prop-types"
import SectionHeader from "./section_header"

import buildFilterContextData from "shared/components/filter_code/build_filter_context_data"
import FilterCodeApis from "./filter_code_api_list"
import EncircledXIcon from "shared/components/icons/encircled_x_icon"
import InlineErrors from "shared/components/inline_errors"
import Mustache from "mustache"

const MonacoEditor = lazy(() => import("shared/components/monaco_editor/index.jsx"))

export default function FilterCodeComposer(props) {
  const {
    actions,
    applet,
    queries,
    services,
    triggers,
    contextTemplate,
    enabled,
    canFilter,
    errors,
    filterCode,
    proPlusSubscriptionUrl,
    onChange,
    docsUrl,
  } = props
  const fieldsetClass = "fieldset composer-fieldset"
  const disabledFieldsetClass = `${fieldsetClass} composer-fieldset-disabled`

  // If the applet already has filter code, we shouldn't apply any visibility
  // restrictions.
  if (filterCode != null && canFilter) {
    const contextData = buildFilterContextData({
      actions,
      applet,
      queries,
      services,
      triggers,
    })

    return (
      <div>
        <div className={fieldsetClass}>
          <div className="partners-tanda-composer field">
            <SectionHeader
              title="Filter code"
              note={
                <>
                  <span>
                    Write JavaScript code to override action fields and skip
                    actions. All actions will run unless you explicitly skip
                    them.{" "}
                    <a
                      href={`${docsUrl}#using-filter-code`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-inline"
                    >
                      Check out the documentation
                    </a>{" "}
                    for more information and examples.
                  </span>
                  {!canFilter && (
                    <span>
                      <br />
                      <br />
                      Note: Filter code is a premium feature. While all users
                      are encouraged to experiment with filter code for personal
                      use, publishing an Applet with filter code requires a paid
                      subscription.
                    </span>
                  )}
                </>
              }
              actions={
                <a
                  onClick={() => confirmAndRemoveCode(filterCode, onChange)}
                  className="partners-tanda-composer-edit-link"
                >
                  <span>
                    <EncircledXIcon /> Remove filter code
                  </span>
                </a>
              }
            />

            <InlineErrors errors={errors} />

            <div className="partners-filter-code-container">
              <Suspense fallback="Loading...">
                <MonacoEditor
                  defaultValue={filterCode}
                  onChange={onChange}
                  typescriptContext={Mustache.render(contextTemplate, contextData)}
                />
              </Suspense>
            </div>
          </div>
        </div>

        <FilterCodeApis contextData={contextData} platform />
      </div>
    )
  }

  if (!enabled) {
    return (
      <div className={disabledFieldsetClass}>
        <div className="composer-add-field field">
          <input
            disabled={true}
            type="button"
            className="button-tertiary"
            value="Add filter code"
            title="Add an action first"
          />

          <div className="composer-add-field-message">
            <b>Add a trigger and an action to unlock adding filter code.</b>{" "}
            Filter code lets you override action fields and skip actions.
          </div>
        </div>
      </div>
    )
  }

  if (canFilter) {
    return (
      <div className={fieldsetClass}>
        <div className="composer-add-field field">
          <input
            onClick={() => onChange("")}
            type="button"
            className="button-tertiary"
            value="Add filter code"
          />
        </div>
      </div>
    )
  } else {
    return (
      <p className="composer-add-field-message">
        <b>Filter code is a Pro+ feature.</b>{" "}
        <a
          href={proPlusSubscriptionUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="link-inline"
        >
          Upgrade
        </a>{" "}
        to use filter code and more in your Applets.
      </p>
    )
  }
}

FilterCodeComposer.propTypes = {
  enabled: PropTypes.bool.isRequired,
  canFilter: PropTypes.bool.isRequired,
  contextTemplate: PropTypes.string.isRequired,
  docsUrl: PropTypes.string.isRequired,
  proPlusSubscriptionUrl: PropTypes.string.isRequired,
  filterCode: PropTypes.string,
  onChange: PropTypes.func,
  errors: InlineErrors.propTypes.errors,
  triggers: PropTypes.object,
  actions: PropTypes.object,
  queries: PropTypes.object,
  services: PropTypes.object,
  applet: PropTypes.object,
}

function confirmAndRemoveCode(filterCode, onChange) {
  if (
    !filterCode ||
    !filterCode.trim() ||
    window.confirm("Are you sure? This will delete your existing code.")
  ) {
    onChange(null)
  }
}
